/* eslint-disable no-unused-vars */

import React, { useCallback, useEffect, useMemo } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Stack, Typography } from '@mui/material';
import MainCard from 'components/MainCard';
import { useFilters, usePagination, useRowSelect, useSortBy, useTable } from 'react-table';
import { Empty, Flex, Tooltip } from 'antd';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useTheme } from '@mui/material/styles';
import LynIcon from 'components/@extended/LynIcon';
import ColumnFilter from 'components/@extended/ColumnFilter';
import SortingIconGroup from 'components/@extended/SortingIconGroup';
import SelectVisibleColumn from 'components/@extended/SelectVisibleColumn';
import { useDispatch, useSelector } from 'react-redux';
// import TablePagination from './DataTablePagination';
import { setDataTableInfo, setScrollIntoTableRow } from 'store/reducer/mainSlice';
import { tableFontSize } from 'components/StyleConstant';
import TablePagination from 'components/LynDataTable/DataTablePagination';
// let clickedRow = { '/comptroller/module': 44 };

export const stickyBody = {
	position: 'sticky',
	left: 0,
	// bgcolor: "rgb(226, 235, 247)",
	borderBottom: '1px solid #ffffff',
	borderRight: '1px solid #ffffff',
	borderColor: '#ffffff',
};
export const stickyHead = {
	py: 1.5,
	position: 'sticky',
	left: 0,
	background: 'white',
	zIndex: 3,
	bgcolor: '#526375',
	color: '#ffffff',
};
export const stickySearch = {
	p: 0.5,
	position: 'sticky',
	left: 0,
	zIndex: 3,
	bgcolor: '#ebebeb',
	// bgcolor: "#c5ccd4",
	// minWidth: "180px",
	// width: "100px",
};

function LeaseDataTable({
	DATA = [],
	defaultVisibleColumn = [],
	onClickHandler,
	COLUMNS = [],
	activeDeactiveHandle,
	deleteHandle,
	rowSelection,
	setRowSelection = () => {},
	xlsxFileName = 'lynorg',
	bulkDelete = true,
	pagination = true,
	firstColStrick = true,
	columnFilterSelector = true,
	current = 0,
	pageSize = 15,
	rowIndexColorMapping = {},
	children,
	tableUpperLeftExtraInfo = null,
	exportButtonShow = true,
}) {
	const location = useLocation();
	const dispatch = useDispatch();
	const clickedRow = useSelector((state) => state.main.clickedItem);
	const limit = useSelector((state) => state.main.limit);
	const pageIndex = useSelector((state) => state.main.page);
	const theme = useTheme();
	const columns = useMemo(() => COLUMNS, [COLUMNS]);
	const data = useMemo(() => DATA ?? [], [DATA]);
	const { pathname } = location;
	const defaultColumn = useMemo(() => {
		return {
			Filter: ColumnFilter,
			sortDescFirst: true,
		};
	}, []);

	const tableInstance = useTable(
		{
			columns,
			data: data ?? [],
			defaultColumn,
			// initialState: { pageIndex: pageIndex?.[pathname] || 0 },
			initialState: { pageIndex: current, pageSize: pageSize, hiddenColumns: defaultVisibleColumn },
			autoResetPage: true,
		},
		useFilters,
		useSortBy,
		usePagination,
		useRowSelect
	);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		page,
		prepareRow,
		allColumns,
		getToggleHideAllColumnsProps,
		selectedFlatRows,
		state,
		filteredRows,
		setPageSize,
		gotoPage,
	} = tableInstance;
	function generateFileName(xlsxFileReName) {
		// Check if filename matches the pattern "<number>_date.xlsx"
		const pattern = /^(\d+)_date$/i;

		if (pattern.test(xlsxFileReName)) {
			// Extract the number from the filename
			const match = xlsxFileReName.match(pattern);
			const numberPart = match[1];

			// Get current date components
			const today = new Date();
			const year = today.getFullYear();
			const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-based
			const day = String(today.getDate()).padStart(2, '0');

			// Format date as YYYY.MM.DD
			const formattedDate = `${year}.${month}.${day}`;

			// Create new filename
			return `${numberPart}_${formattedDate}`;
		}

		// Return original filename if pattern doesn't match
		return xlsxFileReName;
	}

	const dataExport = useCallback(() => {
		const headerArr = headerGroups[0].headers.map((item) => item.Header).filter((item) => item !== 'Action' && typeof item !== 'function');
		const rowsArr = rows.map((item) => {
			const disableColumnKey = [...state.hiddenColumns, 'Selection', 'Action'];
			const obj = { ...item.values };
			disableColumnKey.map((key) => {
				delete obj[key];
				return delete obj[key];
			});
			return Object.values(obj);
		});
		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.json_to_sheet([]);
		XLSX.utils.sheet_add_aoa(ws, [headerArr]);
		XLSX.utils.sheet_add_json(ws, rowsArr, { origin: 'A2', skipHeader: true });
		XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
		XLSX.writeFile(wb, `${generateFileName(xlsxFileName)}.xlsx`);
	}, [state.hiddenColumns, generateFileName]);

	const memorizeClickedItem = (id) => {
		// console.log('id on click', id);
		dispatch(setScrollIntoTableRow({ clickedItem: { [pathname]: id } }));
		dispatch(setDataTableInfo({ page: { [pathname]: state?.pageIndex }, limit: { [pathname]: state?.pageSize } }));
	};

	useEffect(() => {
		setRowSelection(selectedFlatRows);
	}, [selectedFlatRows]);

	useEffect(() => {
		if (state.filters.length === 0) {
			gotoPage(pageIndex?.[pathname] || state?.pageIndex);
		}
		if (pagination) {
			setPageSize(limit?.[pathname] || state?.pageSize);
		}
	}, []);

	// ========> Scroll into view <========
	useEffect(() => {
		const id = clickedRow[pathname];
		if (id) {
			const project = document.getElementById(id);
			if (project) {
				project.classList.add('active');
				project.scrollIntoView({ behavior: 'smooth', block: 'center' });
				dispatch(setScrollIntoTableRow({ clickedItem: {} }));
			}
		}
	}, [allColumns, state.pageIndex, state.pageSize]);

	return (
		<>
			{tableUpperLeftExtraInfo && (
				<Box>
					{columnFilterSelector && tableUpperLeftExtraInfo && (
						<Box sx={{ mt: { xs: 1, sm: 2 } }}>
							{Object.keys(tableUpperLeftExtraInfo || {}).map((key) => (
								<Box key={key} sx={{ display: 'flex' }}>
									<Typography sx={{ width: '100px', fontWeight: 'bold' }}>
										{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
									</Typography>
									<Typography sx={{ width: '30px' }}>: </Typography>
									<Typography>{tableUpperLeftExtraInfo?.[key]}</Typography>
								</Box>
							))}
						</Box>
					)}
				</Box>
			)}
			<Box content="div" sx={{ display: 'flex', justifyContent: 'space-between', mt: { xs: 1, sm: 2 } }}>
				<Box>
					{columnFilterSelector ? (
						<SelectVisibleColumn
							getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
							allColumns={allColumns}
							visibleColumns={headerGroups[0]?.headers}
						/>
					) : (
						children
					)}
				</Box>
				<Flex justify="space-between" align="center" gap=".5rem">
					{rowSelection?.length > 0 && (
						<>
							<div>
								<b style={{ fontSize: '1rem' }}>{rowSelection?.length}</b> {rowSelection?.length > 1 ? 'rows' : 'row'} selected
							</div>

							<Tooltip title={rowSelection?.length > 1 ? 'Activate all' : 'Activate'}>
								<Button
									type="primary"
									style={{ margin: '3px', backgroundColor: theme.palette.lynTheme.success, color: 'white' }}
									onClick={(e) => {
										e.stopPropagation();
										activeDeactiveHandle('Inactive');
									}}
								>
									{rowSelection?.length > 1 ? 'Activate all' : 'Activate'}
								</Button>
							</Tooltip>

							<Tooltip title={rowSelection?.length > 1 ? 'Deactivate all' : 'Deactivate'}>
								<Button
									type="primary"
									style={{ margin: '3px', backgroundColor: theme.palette.lynTheme.submit, color: 'white' }}
									onClick={(e) => {
										e.stopPropagation();
										activeDeactiveHandle('Active');
									}}
								>
									{rowSelection?.length > 1 ? 'Deactivate all' : 'Deactivate'}
								</Button>
							</Tooltip>

							{bulkDelete && (
								<Tooltip title={rowSelection?.length > 1 ? 'Delete all' : 'Delete'}>
									<Button
										type="primary"
										style={{ margin: '3px', backgroundColor: theme.palette.lynTheme.delete, color: 'white' }}
										onClick={(e) => {
											e.stopPropagation();
											if (typeof deleteHandle === 'function') {
												let arr = rowSelection?.map((item) => item?.original);
												deleteHandle(arr);
											}
										}}
									>
										{rowSelection?.length > 1 ? 'Delete all' : 'Delete'}
									</Button>
								</Tooltip>
							)}
						</>
					)}
					{exportButtonShow && (
						<Button
							disableRipple
							color="secondary"
							sx={{ padding: '8px 12px', color: 'text.primary', bgcolor: 'common.white', border: `1px solid ${theme.palette.border}` }}
							aria-label="open profile"
							aria-haspopup="true"
							onClick={dataExport}
						>
							<Stack direction="row" gap={1}>
								<LynIcon folder="icons" fileName={'export'} format="svg" sx={{ marginRight: '4px' }} />{' '}
								<Typography style={{ mt: '12px' }}>Export</Typography>
							</Stack>
						</Button>
					)}
				</Flex>
			</Box>

			<MainCard sx={{ width: '100%', mt: 2, borderColor: '#526375' }} content={false}>
				<TableContainer
					sx={{
						width: '100%',
						maxHeight: `calc(100svh - 330px)`,
						overflowX: 'auto',
						position: 'relative',
						display: 'block',
						'& td, & th': { whiteSpace: 'nowrap' },
						mb: pagination ? '36px' : '0px',
					}}
				>
					<Table
						stickyHeader
						{...getTableProps()}
						sx={{
							'& th:not(:last-of-type)': { borderRight: 1, borderColor: 'common.white' },
						}}
					>
						<TableHead>
							{headerGroups?.map((headerGroup, index) => (
								<TableRow
									{...headerGroup?.getHeaderGroupProps()}
									sx={{ height: '50px', th: { borderBottom: 'none', userSelect: 'none' } }}
									key={index}
								>
									{headerGroup?.headers.map((column, index) => (
										<TableCell
											{...column?.getHeaderProps()}
											sx={
												firstColStrick && index === 0
													? {
															...stickyHead,
															width: column?.width || 'auto',
															fontSize: tableFontSize.headerSize,
															fontWeight: tableFontSize.headerWeight,
														}
													: {
															py: 1.5,
															backgroundColor: '#526375',
															color: '#ffffff',
															width: column?.width || 'auto',
															// fontSize: tableFontSize.cellSize,
															// fontWeight: tableFontSize.cellWeight,
														}
											}
											key={index}
										>
											{/* {console.log(column)} */}
											<Box
												sx={{
													fontSize: tableFontSize.headerSize,
													fontWeight: tableFontSize.headerWeight,
													display: 'flex',
													alignItems: 'center',
													justifyContent: column?.align ? column?.align : 'space-between',
												}}
												{...column.getSortByToggleProps()}
											>
												{column?.render('Header')}
												{column?.canSort && <SortingIconGroup sortingProps={column} />}
											</Box>
										</TableCell>
									))}
								</TableRow>
							))}

							{headerGroups?.map((headerGroup, index) => (
								<TableRow {...headerGroup.getHeaderGroupProps()} sx={{ '& .MuiTableCell-root': { top: '48px' } }} key={index}>
									{headerGroup.headers.map((column, nestedIndex) =>
										column?.id === 'Selection' || column?.id === 'Sl' ? (
											<TableCell
												sx={{
													borderBottom: '1px solid #ffffff',
													backgroundColor: '#ebebeb',
													...(nestedIndex === 0 && { position: 'sticky', left: 0, zIndex: '3' }),
												}}
												key={index + '_' + nestedIndex}
											></TableCell>
										) : (
											<TableCell
												sx={
													firstColStrick && nestedIndex === 0
														? stickySearch
														: {
																p: 0.5,
																backgroundColor: '#ebebeb',
																'&:first-of-type': { backgroundColor: '#ebebeb' },
																'&:last-child': { borderColor: 'white' },
															}
												}
												{...column.getHeaderProps()}
												key={index + '_' + nestedIndex}
											>
												{column.canFilter ? column.render('Filter') : null}
											</TableCell>
										)
									)}
								</TableRow>
							))}
						</TableHead>

						<TableBody {...getTableBodyProps()} key="table_body2">
							{page?.length > 0 ? (
								page?.map((row, index) => {
									prepareRow(row);
									return (
										<TableRow
											hover
											id={row.id}
											{...row?.getRowProps()}
											sx={{
												cursor: 'pointer',
												backgroundColor:
													Object.keys(rowIndexColorMapping).length === 0
														? '#FCFCFC'
														: rowIndexColorMapping[index]?.rowColor,
												'& .MuiTableCell-root:first-of-type': {
													bgcolor:
														Object.keys(rowIndexColorMapping).length === 0
															? '#FCFCFC'
															: rowIndexColorMapping[index]?.rowColor,
													borderBottom: '1px solid #E0E0E0',
												},
												'&:hover .MuiTableCell-root:first-of-type': { bgcolor: '#f5f5f5' },

												'&.active .MuiTableCell-root:first-of-type': { bgcolor: '#ffffff' },
											}}
											onClick={() => memorizeClickedItem(row.id)}
											key={index}
										>
											{row?.cells?.map((cell, index) => (
												// "&:nth-of-type(even)": { backgroundColor: "#eaefeb" }, "&:nth-of-type(odd)": { backgroundColor: "rgb(226, 235, 247)" }
												<TableCell
													sx={index === 0 && firstColStrick ? stickyBody : { borderBottom: '1px solid #E0E0E0' }}
													{...cell.getCellProps()}
													onClick={() => {
														if (!row?.cells[index]?.column?.onClicked) {
															onClickHandler(row.original);
														}
													}}
													key={index}
												>
													{cell?.column?.id === 'Selection' || cell?.column?.id === 'Sl' ? (
														<Box
															component="div"
															sx={{
																textAlign: cell?.column?.cellAlign || 'left',
															}}
														>
															{cell.render('Cell')}
														</Box>
													) : (
														<Box
															component="div"
															sx={{
																fontSize: tableFontSize.cellSize,
																fontWeight: tableFontSize.cellWeight,
																display: 'block',
																maxWidth: '337px',
																overflow: 'hidden',
																whiteSpace: 'nowrap',
																textOverflow: 'ellipsis',
																textAlign: cell?.column?.cellAlign || 'left',
															}}
														>
															{cell.render('Cell')}
														</Box>
													)}
												</TableCell>
											))}
										</TableRow>
									);
								})
							) : (
								<TableRow>
									<TableCell colSpan={columns?.length}>
										<Box
											component="div"
											sx={{
												width: '100%',
												display: 'flex',
												flexDirection: 'column',
												justifyContent: 'center',
												alignItems: 'center',
												height: '350px',
											}}
										>
											<Empty />
										</Box>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>

				{pagination && (
					<Box sx={{ position: 'absolute', left: 0, bottom: 0, width: '100%', backgroundColor: '#526375' }}>
						<TablePagination
							sx={{
								'& .MuiTablePagination-root': {
									color: '#fff',
								},
							}}
							{...{
								gotoPage: gotoPage,
								setPageSize: setPageSize,
								state: state,
								count: filteredRows?.length,
								pathname,
							}}
						/>
					</Box>
				)}
			</MainCard>
		</>
	);
}

export default React.memo(LeaseDataTable);
